import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
import { ko } from 'date-fns/locale';
import { IoChevronBack, IoChevronForward, IoAdd, IoClose, IoCalendarOutline, IoPeopleOutline, IoSearchOutline, IoMapOutline, IoTrashOutline, IoCreateOutline, IoInformationCircleOutline } from 'react-icons/io5';

const COLORS = {
  primary: '#4A6741',
  active: '#E85830',
  header: '#2C3640',
  background: '#F4F3F8',
  card: '#FFFFFF',
  gold: '#C5A55A',
};


// 관리자가 설정한 색상(Firestore)을 우선 사용, 없으면 해시 폴백
const DEFAULT_COLORS = [
  { bg: '#EDF2EB', text: '#4A6741' },
  { bg: '#FFF0EC', text: '#E85830' },
  { bg: '#EBF0F7', text: '#3B6DAA' },
  { bg: '#FFF8E6', text: '#B8860B' },
  { bg: '#F3EBF7', text: '#7B4D9E' },
  { bg: '#E6F7F2', text: '#2A8C6A' },
  { bg: '#FFF0F5', text: '#C44D7B' },
  { bg: '#F0F4E8', text: '#6B8E23' },
];

// 단색 → bg/text 쌍으로 변환
const colorToPair = (hex) => {
  if (!hex) return DEFAULT_COLORS[0];
  return { bg: `${hex}20`, text: hex };
};

const hashColor = (companyName) => {
  if (!companyName) return DEFAULT_COLORS[0];
  let hash = 0;
  for (let i = 0; i < companyName.length; i++) {
    hash = companyName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return DEFAULT_COLORS[Math.abs(hash) % DEFAULT_COLORS.length];
};

const SchedulePage = () => {
  const { userData } = useAuth();
  const { isAdmin } = useRole();
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', address: '', addressDetail: '', memo: '', isPublic: true, timeType: '종일', startTime: '', endTime: '', dateType: 'single', startDate: '', endDate: '', editGroupId: null });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [mapTarget, setMapTarget] = useState(null); // 클릭한 일정
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [savedColors, setSavedColors] = useState({});

  // Firestore에서 관리자 설정 색상 로드
  useEffect(() => {
    const loadColors = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'scheduleColors'));
        if (snap.exists()) setSavedColors(snap.data());
      } catch (err) { /* 무시 */ }
    };
    loadColors();
  }, []);

  const getCompanyColor = useCallback((companyName) => {
    if (savedColors[companyName]) return colorToPair(savedColors[companyName]);
    return hashColor(companyName);
  }, [savedColors]);

  const openMap = useCallback((target, allSchedules) => {
    setMapTarget(target);
    setTimeout(() => {
      const kakao = window.kakao;
      if (!kakao || !kakao.maps || !mapContainerRef.current) return;

      const center = new kakao.maps.LatLng(target.lat, target.lng);
      const map = new kakao.maps.Map(mapContainerRef.current, {
        center,
        level: 5,
      });
      mapRef.current = map;

      const overlays = [];

      // 모든 일정 마커 + 커스텀 오버레이
      allSchedules.forEach(s => {
        if (!s.lat || !s.lng) return;
        const pos = new kakao.maps.LatLng(s.lat, s.lng);
        const isMine = s.isMine;
        const color = isMine ? '#4A6741' : '#E85830';
        const bgLight = isMine ? '#EDF2EB' : '#FFF0EC';

        // 축소 시 (간략) 오버레이
        const briefContent = document.createElement('div');
        briefContent.innerHTML = `
          <div style="
            background:${color};color:white;padding:4px 10px;border-radius:12px;
            font-size:11px;font-weight:600;white-space:nowrap;
            box-shadow:0 2px 6px rgba(0,0,0,0.2);cursor:pointer;
            transform:translateY(-42px);
          ">${s.companyName || '내 일정'}</div>
        `;

        const briefOverlay = new kakao.maps.CustomOverlay({
          map,
          position: pos,
          content: briefContent,
          yAnchor: 0,
        });

        // 확대 시 (상세) 오버레이
        const detailContent = document.createElement('div');
        detailContent.innerHTML = `
          <div style="
            background:white;border-radius:12px;padding:12px 14px;min-width:180px;max-width:240px;
            box-shadow:0 4px 16px rgba(0,0,0,0.15);
            transform:translateY(-48px);cursor:pointer;
          ">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
              <span style="
                background:${bgLight};color:${color};padding:2px 8px;border-radius:8px;
                font-size:10px;font-weight:700;
              ">${isMine ? '내 일정' : s.companyName}</span>
              <span style="font-size:11px;color:#8C9AA4;">${s.time || '종일'}</span>
            </div>
            <p style="font-size:13px;font-weight:700;color:#1E2A32;margin-bottom:2px;line-height:1.4;">${s.title}</p>
            ${s.address ? `<p style="font-size:11px;color:#8C9AA4;margin-top:2px;">${s.address}</p>` : ''}
            ${s.memo ? `<p style="font-size:11px;color:#B0B8BF;margin-top:3px;">${s.memo}</p>` : ''}
          </div>
        `;

        const detailOverlay = new kakao.maps.CustomOverlay({
          map: null, // 초기엔 안 보임
          position: pos,
          content: detailContent,
          yAnchor: 0,
        });

        overlays.push({ brief: briefOverlay, detail: detailOverlay });

        // 마커 (카카오맵 기본 마커)
        const marker = new kakao.maps.Marker({
          map,
          position: pos,
        });

        kakao.maps.event.addListener(marker, 'click', () => {
          // 모든 상세 닫고 이것만 열기
          overlays.forEach(o => o.detail.setMap(null));
          detailOverlay.setMap(map);
          map.setCenter(pos);
        });
      });

      // 줌 레벨에 따라 간략/상세 전환
      const updateOverlays = () => {
        const level = map.getLevel();
        overlays.forEach(({ brief, detail }) => {
          if (level <= 5) {
            brief.setMap(null);
            detail.setMap(map);
          } else {
            detail.setMap(null);
            brief.setMap(map);
          }
        });
      };

      updateOverlays();
      kakao.maps.event.addListener(map, 'zoom_changed', updateOverlays);
    }, 100);
  }, []);

  const openAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: (data) => {
        const addr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
        setForm(prev => ({ ...prev, address: addr }));
      },
    }).open();
  };

  useEffect(() => {
    if (location.state?.openForm) {
      setEditingId(null);
      setForm(emptyForm);
      setShowForm(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const monthStart = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(currentDate), 'yyyy-MM-dd');

      const q = query(collection(db, 'schedules'),
        where('date', '>=', monthStart),
        where('date', '<=', monthEnd)
      );
      const snap = await getDocs(q);
      const uid = userData?.uid;
      const all = snap.docs.map(d => ({ id: d.id, ...d.data(), isMine: d.data().uid === uid }));

      // 관리자: 전체, 일반사용자: 내 일정 + 공개 일정 (숨김 제외)
      if (isAdmin) {
        setSchedules(all);
      } else {
        setSchedules(all.filter(s => !s.hidden && (s.uid === uid || s.isPublic)));
      }
    } catch (err) {
      console.error('일정 로드 실패:', err);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const emptyForm = { title: '', address: '', addressDetail: '', memo: '', isPublic: true, timeType: '종일', startTime: '', endTime: '', dateType: 'single', startDate: '', endDate: '', editGroupId: null };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const openEdit = (s) => {
    // 시간 파싱: "09:00~18:00" → startTime/endTime, 그 외 → timeType
    let timeType = s.time || '종일';
    let startTime = '';
    let endTime = '';
    if (s.time && s.time.includes('~')) {
      const [st, et] = s.time.split('~');
      startTime = st.replace(':', '');
      endTime = et.replace(':', '');
      timeType = '시간지정';
    } else if (['종일', '오전내내', '오후내내'].includes(s.time)) {
      timeType = s.time;
    }

    // groupId가 있으면 기간 범위 자동 채우기
    let dateType = 'single';
    let editStartDate = s.date || '';
    let editEndDate = '';
    let editGroupId = s.groupId || null;

    if (s.groupId) {
      const groupSchedules = schedules.filter(gs => gs.groupId === s.groupId);
      const sortedDates = groupSchedules.map(gs => gs.date).sort();
      dateType = 'range';
      editStartDate = sortedDates[0];
      editEndDate = sortedDates[sortedDates.length - 1];
    }

    setEditingId(s.id);
    setForm({
      title: s.title || '',
      address: s.address || '',
      addressDetail: '',
      memo: s.memo || '',
      isPublic: s.isPublic !== false,
      timeType,
      startTime,
      endTime,
      dateType,
      startDate: editStartDate,
      endDate: editEndDate,
      editGroupId,
    });
    setShowForm(true);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleSubmit = async () => {
    if (!form.title) {
      showToast('현장명을 입력해주세요');
      return;
    }
    if (form.dateType === 'range' && !form.endDate) {
      showToast('시공기간 종료일을 입력해주세요');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const fullAddress = form.addressDetail
        ? `${form.address} ${form.addressDetail}`
        : form.address;
      let time = form.timeType;
      if (form.timeType === '시간지정') {
        const start = form.startTime.replace(/(\d{2})(\d{2})/, '$1:$2');
        const end = form.endTime.replace(/(\d{2})(\d{2})/, '$1:$2');
        time = `${start}~${end}`;
      }

      if (editingId) {
        // 수정
        const baseData = {
          title: form.title,
          address: fullAddress,
          memo: form.memo,
          time,
          isPublic: form.isPublic,
        };

        if (form.dateType === 'range' && form.endDate) {
          // 기간 수정: 기존 그룹 삭제 → 새 기간으로 재생성
          if (form.editGroupId) {
            const gq = query(collection(db, 'schedules'), where('groupId', '==', form.editGroupId));
            const gSnap = await getDocs(gq);
            for (const gd of gSnap.docs) {
              await deleteDoc(doc(db, 'schedules', gd.id));
            }
          } else {
            await deleteDoc(doc(db, 'schedules', editingId));
          }
          const baseDate = new Date(form.startDate);
          const endDate = new Date(form.endDate);
          const groupId = `grp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          const dates = eachDayOfInterval({ start: baseDate, end: endDate });
          // 기존 일정의 uid/companyName 유지
          const origSchedule = schedules.find(s => s.id === editingId);
          for (const d of dates) {
            await addDoc(collection(db, 'schedules'), {
              ...baseData,
              uid: origSchedule?.uid || userData.uid,
              companyName: origSchedule?.companyName || userData.companyName || userData.name,
              date: format(d, 'yyyy-MM-dd'),
              groupId,
              createdAt: serverTimestamp(),
            });
          }
        } else {
          // 단일 날짜 수정
          if (form.editGroupId) {
            // 기간 → 단일로 변경: 기존 그룹 삭제 후 1개만 생성
            const gq = query(collection(db, 'schedules'), where('groupId', '==', form.editGroupId));
            const gSnap = await getDocs(gq);
            for (const gd of gSnap.docs) {
              if (gd.id !== editingId) await deleteDoc(doc(db, 'schedules', gd.id));
            }
            await updateDoc(doc(db, 'schedules', editingId), {
              ...baseData,
              date: form.startDate || format(selectedDate, 'yyyy-MM-dd'),
              groupId: null,
            });
          } else {
            await updateDoc(doc(db, 'schedules', editingId), {
              ...baseData,
              date: form.startDate || format(selectedDate, 'yyyy-MM-dd'),
            });
          }
        }
      } else {
        // 신규
        const baseData = {
          uid: userData.uid,
          companyName: userData.companyName || userData.name,
          title: form.title,
          address: fullAddress,
          memo: form.memo,
          time,
          isPublic: form.isPublic,
          createdAt: serverTimestamp(),
        };

        const baseDate = form.startDate ? new Date(form.startDate) : selectedDate;

        if (form.dateType === 'range' && form.endDate) {
          const end = new Date(form.endDate);
          if (end >= baseDate) {
            const groupId = `grp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            const dates = eachDayOfInterval({ start: baseDate, end });
            for (const d of dates) {
              await addDoc(collection(db, 'schedules'), { ...baseData, date: format(d, 'yyyy-MM-dd'), groupId });
            }
          }
        } else {
          await addDoc(collection(db, 'schedules'), { ...baseData, date: format(baseDate, 'yyyy-MM-dd') });
        }
      }

      closeForm();
      loadSchedules();
    } catch (err) {
      console.error('일정 저장 실패:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 날짜의 일정을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'schedules', id));
      loadSchedules();
    } catch (err) {
      console.error('일정 삭제 실패:', err);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('이 기간의 모든 일정을 삭제하시겠습니까?')) return;
    try {
      const q = query(collection(db, 'schedules'), where('groupId', '==', groupId));
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        await deleteDoc(doc(db, 'schedules', d.id));
      }
      loadSchedules();
    } catch (err) {
      console.error('기간 일정 삭제 실패:', err);
    }
  };

  const days = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate),
  });

  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const selectedSchedules = schedules.filter(s => s.date === selectedDateStr);
  const mySchedules = selectedSchedules.filter(s => s.isMine);
  const otherSchedules = selectedSchedules.filter(s => !s.isMine);
  const firstDayOffset = startOfMonth(currentDate).getDay();

  // 주(week) 단위로 날짜 분할 (빈 셀 포함)
  const allCells = [...Array(firstDayOffset).fill(null), ...days];
  while (allCells.length % 7 !== 0) allCells.push(null);
  const weeks = [];
  for (let i = 0; i < allCells.length; i += 7) {
    weeks.push(allCells.slice(i, i + 7));
  }

  // 일정 바 데이터 구성 (groupId로 기간 합산)
  const barItems = React.useMemo(() => {
    const groups = {};
    const singles = [];
    schedules.forEach(s => {
      if (s.groupId) {
        if (!groups[s.groupId]) {
          groups[s.groupId] = { ...s, dates: [s.date] };
        } else {
          groups[s.groupId].dates.push(s.date);
        }
      } else {
        singles.push({ ...s, startDate: s.date, endDate: s.date });
      }
    });
    const merged = Object.values(groups).map(g => {
      const sorted = g.dates.sort();
      return { ...g, startDate: sorted[0], endDate: sorted[sorted.length - 1] };
    });
    return [...merged, ...singles];
  }, [schedules]);

  // 주별 바 레인 배치 (최대 MAX_BARS개)
  const MAX_BARS = 5;
  const getWeekBars = (week) => {
    const weekStart = week.find(d => d !== null);
    const weekEnd = [...week].reverse().find(d => d !== null);
    if (!weekStart || !weekEnd) return [];
    const ws = format(weekStart, 'yyyy-MM-dd');
    const we = format(weekEnd, 'yyyy-MM-dd');

    return barItems
      .filter(b => b.startDate <= we && b.endDate >= ws)
      .map(b => {
        const startCol = week.findIndex(d => d && format(d, 'yyyy-MM-dd') >= b.startDate);
        const endCol = 6 - [...week].reverse().findIndex(d => d && format(d, 'yyyy-MM-dd') <= b.endDate);
        const cc = getCompanyColor(b.companyName);
        return {
          ...b,
          col: Math.max(0, startCol) + 1,
          colEnd: Math.min(7, endCol) + 2,
          color: '#fff',
          bg: cc.text,
        };
      });
  };

  return (
    <Container>
      <MonthNav>
        <MonthNavBtn onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
          <IoChevronBack size={18} />
        </MonthNavBtn>
        <MonthTitle>{format(currentDate, 'yyyy년 M월', { locale: ko })}</MonthTitle>
        <MonthNavBtn onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
          <IoChevronForward size={18} />
        </MonthNavBtn>
      </MonthNav>

      <CalendarWrap>
        <WeekHeader>
          {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
            <DayLabel key={d} $isSun={i === 0} $isSat={i === 6}>{d}</DayLabel>
          ))}
        </WeekHeader>
        {weeks.map((week, wi) => {
          const bars = getWeekBars(week);
          const barRows = [];
          const lanes = [];
          bars.forEach(bar => {
            let lane = lanes.findIndex(l => l <= bar.col);
            if (lane === -1) lane = lanes.length;
            lanes[lane] = bar.colEnd;
            if (!barRows[lane]) barRows[lane] = [];
            barRows[lane].push(bar);
          });
          return (
            <WeekRow key={wi}>
              <WeekDayRow>
                {week.map((day, di) => {
                  if (!day) return <DayCellEmpty key={`e-${wi}-${di}`} />;
                  const isSelected = isSameDay(day, selectedDate);
                  const dayOfWeek = day.getDay();
                  return (
                    <DayCell key={format(day, 'yyyy-MM-dd')} onClick={() => setSelectedDate(day)}>
                      <DayNum $active={isSelected} $isSun={dayOfWeek === 0} $isSat={dayOfWeek === 6}>
                        {format(day, 'd')}
                      </DayNum>
                    </DayCell>
                  );
                })}
              </WeekDayRow>
              {barRows.map((row, ri) => (
                <WeekBarRow key={ri}>
                  {row.map(bar => (
                    <CalBar
                      key={bar.id || bar.groupId}
                      $col={bar.col}
                      $colEnd={bar.colEnd}
                      $bg={bar.bg}
                      $color={bar.color}
                      onClick={() => {
                        const d = days.find(dd => format(dd, 'yyyy-MM-dd') === bar.startDate);
                        if (d) setSelectedDate(d);
                      }}
                    >
                      {bar.title}
                    </CalBar>
                  ))}
                </WeekBarRow>
              ))}
              {barRows.length === 0 && <WeekBarSpacer />}
            </WeekRow>
          );
        })}
      </CalendarWrap>

      <SelectedDateArea>
        <SelectedDateTitle>
          {format(selectedDate, 'M월 d일 (EEE)', { locale: ko })}
        </SelectedDateTitle>
      </SelectedDateArea>

      <Section>
        <SectionHeader>
          <SectionTitleRow><IoCalendarOutline size={18} /><SectionTitle>나의 일정</SectionTitle></SectionTitleRow>
          <SectionCount>{mySchedules.length}건</SectionCount>
        </SectionHeader>
        {mySchedules.length === 0 ? (
          <EmptyText>등록된 일정이 없습니다</EmptyText>
        ) : (
          mySchedules.map((s, i) => {
            const cc = getCompanyColor(s.companyName);
            return (
            <ScheduleItem key={s.id} $bgColor={`${cc.text}30`} onClick={() => s.lat && openMap(s, selectedSchedules)}>
              <ScheduleTime>{s.time || '종일'}</ScheduleTime>
              <ScheduleItemInfo>
                <ScheduleCompany $color={cc.text}>{s.companyName}</ScheduleCompany>
                <ScheduleItemTitle>{s.title}</ScheduleItemTitle>
                {s.address && <ScheduleAddress>{s.address}</ScheduleAddress>}
              </ScheduleItemInfo>
              {s.isPublic === false && <PrivateTag>비공개</PrivateTag>}
              {s.lat && <MapIcon><IoMapOutline size={16} /></MapIcon>}
              {(s.uid === userData?.uid || isAdmin) && (
                <DeleteBtnGroup>
                  <ScheduleEditBtn onClick={(e) => { e.stopPropagation(); openEdit(s); }}>
                    <IoCreateOutline size={13} />
                    수정
                  </ScheduleEditBtn>
                  <ScheduleDeleteBtn onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}>
                    <IoTrashOutline size={13} />
                    당일삭제
                  </ScheduleDeleteBtn>
                  {s.groupId && (
                    <ScheduleDeleteBtn $group onClick={(e) => { e.stopPropagation(); handleDeleteGroup(s.groupId); }}>
                      <IoTrashOutline size={13} />
                      기간삭제
                    </ScheduleDeleteBtn>
                  )}
                </DeleteBtnGroup>
              )}
            </ScheduleItem>
            );
          })
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitleRow><IoPeopleOutline size={18} /><SectionTitle>다른 업체 일정</SectionTitle></SectionTitleRow>
          <SectionCount>{otherSchedules.length}건</SectionCount>
        </SectionHeader>
        {otherSchedules.length === 0 ? (
          <EmptyText>공개된 일정이 없습니다</EmptyText>
        ) : (
          otherSchedules.map((s, i) => {
            const cc = getCompanyColor(s.companyName);
            return (
            <ScheduleItem key={s.id} $bgColor={`${cc.text}30`} onClick={() => s.lat && openMap(s, selectedSchedules)}>
              <ScheduleTime>{s.time || '종일'}</ScheduleTime>
              <ScheduleItemInfo>
                <ScheduleCompany $color={cc.text}>{s.companyName}</ScheduleCompany>
                <ScheduleItemTitle>{s.title}</ScheduleItemTitle>
                {s.address && <ScheduleAddress>{s.address}</ScheduleAddress>}
              </ScheduleItemInfo>
              {s.lat && <MapIcon><IoMapOutline size={16} /></MapIcon>}
              {isAdmin && (
                <DeleteBtnGroup>
                  <ScheduleDeleteBtn onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}>
                    <IoTrashOutline size={13} />
                    당일삭제
                  </ScheduleDeleteBtn>
                  {s.groupId && (
                    <ScheduleDeleteBtn $group onClick={(e) => { e.stopPropagation(); handleDeleteGroup(s.groupId); }}>
                      <IoTrashOutline size={13} />
                      기간삭제
                    </ScheduleDeleteBtn>
                  )}
                </DeleteBtnGroup>
              )}
            </ScheduleItem>
            );
          })
        )}
      </Section>

      {mapTarget && (
        <MapOverlay>
          <MapHeader>
            <MapTitle>{format(selectedDate, 'M월 d일', { locale: ko })} 일정 지도</MapTitle>
            <MapCloseBtn onClick={() => setMapTarget(null)}><IoClose size={22} /></MapCloseBtn>
          </MapHeader>
          <MapContainer ref={mapContainerRef} />
          <MapLegend>
            <LegendItem><LegendDot $color={COLORS.primary} />내 일정</LegendItem>
            <LegendItem><LegendDot $color={COLORS.active} />타사 일정</LegendItem>
          </MapLegend>
        </MapOverlay>
      )}

      {showForm && (
        <FormOverlay onClick={closeForm}>
          <FormSheet onClick={e => e.stopPropagation()}>
            <SheetHandle />
            <FormTitle>{editingId ? '일정 수정' : '일정 추가'}</FormTitle>

            <FormLabel>날짜 {form.dateType === 'range' && <RequiredTag>(필수입력)</RequiredTag>}</FormLabel>
            <DateTypeRow>
                <DateTypeChip $active={form.dateType === 'single'} onClick={() => setForm({ ...form, dateType: 'single', endDate: '' })}>
                  선택 날짜
                </DateTypeChip>
                <DateTypeChip $active={form.dateType === 'range'} onClick={() => setForm({ ...form, dateType: 'range' })}>
                  기간 지정
                </DateTypeChip>
              </DateTypeRow>
            {form.dateType === 'single' ? (
              <DateInput
                type="date"
                value={form.startDate || format(selectedDate, 'yyyy-MM-dd')}
                onChange={e => setForm({ ...form, startDate: e.target.value })}
                onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
              />
            ) : (
              <DateRangeRow>
                <DateInput
                  type="date"
                  value={form.startDate || format(selectedDate, 'yyyy-MM-dd')}
                  onChange={e => setForm({ ...form, startDate: e.target.value })}
                  onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                />
                <DateRangeSep>~</DateRangeSep>
                <DateInput
                  type="date"
                  value={form.endDate}
                  min={form.startDate || format(selectedDate, 'yyyy-MM-dd')}
                  onChange={e => setForm({ ...form, endDate: e.target.value })}
                  onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                />
              </DateRangeRow>
            )}

            <FormLabel>현장명 *</FormLabel>
            <FormInput
              placeholder="현장명을 입력하세요"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
            />

            <FormLabel>시간</FormLabel>
            <TimeChipRow>
              {['종일', '오전내내', '오후내내', '시간지정'].map(opt => (
                <TimeChip
                  key={opt}
                  $active={form.timeType === opt}
                  onClick={() => setForm({ ...form, timeType: opt, startTime: '', endTime: '' })}
                >
                  {opt}
                </TimeChip>
              ))}
            </TimeChipRow>
            {form.timeType === '시간지정' && (
              <TimeInputRow>
                <TimeInput
                  placeholder="0900"
                  value={form.startTime}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setForm({ ...form, startTime: v });
                  }}
                  inputMode="numeric"
                  maxLength={4}
                  onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                />
                <TimeSeparator>~</TimeSeparator>
                <TimeInput
                  placeholder="1800"
                  value={form.endTime}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setForm({ ...form, endTime: v });
                  }}
                  inputMode="numeric"
                  maxLength={4}
                  onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                />
              </TimeInputRow>
            )}
            {form.timeType === '시간지정' && (
              <TimeHint>
                {form.startTime.length === 4 && form.endTime.length === 4
                  ? `${form.startTime.slice(0,2)}:${form.startTime.slice(2,4)} ~ ${form.endTime.slice(0,2)}:${form.endTime.slice(2,4)}`
                  : '시작·종료 각 4자리 입력 (예: 0900, 1800)'}
              </TimeHint>
            )}

            <FormLabel>주소</FormLabel>
            <AddressRow>
              <AddressDisplay
                $hasValue={!!form.address}
                onClick={openAddressSearch}
              >
                {form.address || '주소를 검색하세요'}
              </AddressDisplay>
              <AddressSearchBtn onClick={openAddressSearch}>
                <IoSearchOutline size={18} />
              </AddressSearchBtn>
            </AddressRow>
            {form.address && (
              <FormInput
                placeholder="상세주소 입력"
                value={form.addressDetail}
                onChange={e => setForm({ ...form, addressDetail: e.target.value })}
                style={{ marginTop: 8 }}
                onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
              />
            )}

            <FormLabel>메모</FormLabel>
            <FormTextarea
              placeholder="메모"
              value={form.memo}
              onChange={e => setForm({ ...form, memo: e.target.value })}
              rows={3}
              onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
            />

            <ToggleRow>
              <ToggleLabel>
                <ToggleLabelText>공개 여부</ToggleLabelText>
                <ToggleDesc>
                  {form.isPublic ? '다른 업체에서도 볼 수 있음' : '나만 볼 수 있음'}
                </ToggleDesc>
              </ToggleLabel>
              <ToggleSwitch $active={form.isPublic} onClick={() => setForm({ ...form, isPublic: !form.isPublic })}>
                <ToggleThumb $active={form.isPublic} />
              </ToggleSwitch>
            </ToggleRow>

            <FormSubmitBtn onClick={handleSubmit} disabled={!form.title || submitting}>
              {submitting ? '저장 중...' : editingId ? '수정 완료' : '저장하기'}
            </FormSubmitBtn>
          </FormSheet>
        </FormOverlay>
      )}

      {toast && (
        <ToastWrap>
          <ToastBox>
            <IoInformationCircleOutline size={18} color="#E85830" />
            <ToastText>{toast}</ToastText>
          </ToastBox>
        </ToastWrap>
      )}
    </Container>
  );
};

export default SchedulePage;

/* ─── Styled Components ──────────────────── */

const Container = styled.div`
  min-height: 100vh;
  background: ${COLORS.background};
  padding-bottom: 100px;
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: ${COLORS.background};
`;

const MonthNavBtn = styled.button`
  color: ${COLORS.header};
  display: flex;
  padding: 6px;
`;

const MonthTitle = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.header};
`;

const CalendarWrap = styled.div`
  background: white;
  margin: 0 12px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
`;

const WeekHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 10px 4px 6px;
  border-bottom: 1px solid #F0F0EE;
`;

const DayLabel = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $isSun, $isSat }) => $isSun ? '#E85830' : $isSat ? '#5C7A8A' : '#999'};
`;

const WeekRow = styled.div`
  border-bottom: 1px solid #F5F5F3;
  &:last-child { border-bottom: none; }
`;

const WeekDayRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const DayCellEmpty = styled.div`
  padding: 12px 0;
`;

const DayCell = styled.div`
  text-align: center;
  padding: 12px 0 4px;
  cursor: pointer;
`;

const DayNum = styled.span`
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? '700' : '400'};
  color: ${({ $active, $isSun, $isSat }) =>
    $active ? '#fff' :
    $isSun ? '#E85830' :
    $isSat ? '#5C7A8A' :
    '#333'
  };
  ${({ $active }) => $active ? `
    background: ${COLORS.primary};
    border-radius: 8px;
    padding: 2px 7px;
  ` : ''}
`;

const WeekBarRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 0 2px;
  min-height: 20px;
`;

const WeekBarSpacer = styled.div`
  min-height: 12px;
`;

const CalBar = styled.div`
  grid-column: ${({ $col, $colEnd }) => `${$col} / ${$colEnd}`};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-size: 10px;
  font-weight: 600;
  padding: 3px 5px;
  border-radius: 4px;
  margin: 1px 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1.3;
`;

const SelectedDateArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 8px;
`;

const SelectedDateTitle = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: ${COLORS.header};
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  border-radius: 8px;
  background: ${COLORS.header};
  color: white;
  font-size: 13px;
  font-weight: 600;
`;

const Section = styled.div`
  background: white;
  margin: 8px 12px;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.header};
`;

const SectionTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.header};
`;

const SectionCount = styled.span`
  font-size: 13px;
  color: #8C9AA4;
`;

const EmptyText = styled.p`
  font-size: 13px;
  color: #bbb;
  text-align: center;
  padding: 24px 0;
`;

const ScheduleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  border-radius: 12px;
  background: ${({ $bgColor }) => $bgColor || 'white'};
  cursor: pointer;
  &:last-child { margin-bottom: 0; }
  &:active { opacity: 0.7; }
`;

const ScheduleColorBar = styled.div`
  width: 3px;
  height: 32px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ScheduleTime = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.header};
  min-width: 44px;
  flex-shrink: 0;
`;

const ScheduleItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScheduleItemTitle = styled.p`
  font-size: 14px;
  color: #1E2A32;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScheduleCompany = styled.p`
  font-size: 12px;
  color: ${({ $color }) => $color || '#8C9AA4'};
  font-weight: 600;
  margin-bottom: 2px;
`;

const ScheduleAddress = styled.p`
  font-size: 11px;
  color: #AAB4BC;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MapIcon = styled.div`
  color: #8C9AA4;
  flex-shrink: 0;
  display: flex;
`;

const PrivateTag = styled.span`
  font-size: 10px;
  color: #999;
  background: #F0F0F0;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
`;

const DeleteBtn = styled.button`
  color: #ccc;
  display: flex;
  flex-shrink: 0;
  &:hover { color: #EF5350; }
`;

const DeleteBtnGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
`;

const ScheduleEditBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 10px;
  border-radius: 6px;
  background: #E8F5E9;
  color: #4A6741;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
`;

const ScheduleDeleteBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 10px;
  border-radius: 6px;
  background: ${p => p.$group ? '#FFF3E0' : '#FFF0EE'};
  color: ${p => p.$group ? '#E65100' : '#EF5350'};
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
`;

/* ─── Map Overlay ──────────────────── */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const MapOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: white;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease;
`;

const MapHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${COLORS.header};
  color: white;
  flex-shrink: 0;
`;

const MapTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
`;

const MapCloseBtn = styled.button`
  color: white;
  display: flex;
`;

const MapContainer = styled.div`
  flex: 1;
  width: 100%;
`;

const MapLegend = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  padding: 10px;
  background: white;
  border-top: 1px solid #F0F0EE;
  flex-shrink: 0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #5A6B75;
`;

const LegendDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

/* ─── Bottom Sheet Form ──────────────────── */

const AddressRow = styled.div`
  display: flex;
  gap: 8px;
`;

const AddressDisplay = styled.div`
  flex: 1;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  color: ${({ $hasValue }) => $hasValue ? '#333' : '#bbb'};
  background: #FAFAFA;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AddressSearchBtn = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #F0F0F0;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FormOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const FormSheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 16px 16px 0 0;
  padding: 12px 24px 32px;
  max-height: 85vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  animation: slideUp 0.25s ease-out;
  @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
  margin: 0 auto 16px;
`;

const FormTitle = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: ${COLORS.header};
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
  margin-top: 12px;
`;

const DateTypeRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const DateTypeChip = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: ${p => p.$active ? '600' : '400'};
  background: ${p => p.$active ? COLORS.primary : '#F0F0F0'};
  color: ${p => p.$active ? '#fff' : '#666'};
  border: 1px solid ${p => p.$active ? COLORS.primary : '#E0E0E0'};
`;

const DateDisplay = styled.span`
  font-size: 14px;
  color: #1E2A32;
  font-weight: 500;
  padding: 8px 0;
`;

const DateRangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DateRangeSep = styled.span`
  font-size: 14px;
  color: #999;
  flex-shrink: 0;
`;

const DateInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 8px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  font-size: 13px;
  color: #333;
  box-sizing: border-box;
  &:focus { border-color: ${COLORS.primary}; outline: none; }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  &:focus { border-color: #999; outline: none; }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  resize: none;
  &:focus { border-color: #999; outline: none; }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding: 14px 0;
  border-top: 1px solid #f0f0f0;
`;

const ToggleLabel = styled.div``;

const ToggleLabelText = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const ToggleDesc = styled.p`
  font-size: 12px;
  color: #999;
  margin-top: 2px;
`;

const ToggleSwitch = styled.div`
  width: 48px;
  height: 28px;
  border-radius: 14px;
  background: ${({ $active }) => $active ? '#333' : '#ddd'};
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
`;

const ToggleThumb = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 3px;
  left: ${({ $active }) => $active ? '23px' : '3px'};
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const TimeChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TimeChip = styled.button`
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  background: ${({ $active }) => $active ? '#333' : '#F0F0F0'};
  color: ${({ $active }) => $active ? 'white' : '#666'};
  transition: all 0.15s;
`;

const TimeInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
`;

const TimeInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 10px 0;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 2px;
  &:focus { border-color: #999; outline: none; }
`;

const TimeSeparator = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #999;
`;

const TimeHint = styled.div`
  font-size: 13px;
  color: #999;
  margin-top: 8px;
`;

const FormSubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 20px;
  border-radius: 28px;
  background: ${COLORS.primary};
  color: white;
  font-size: 15px;
  font-weight: 600;
  &:disabled { opacity: 0.5; }
`;

const RequiredTag = styled.span`
  color: #E85830;
  font-size: 11px;
  font-weight: 500;
  margin-left: 4px;
`;

const ToastWrap = styled.div`
  position: fixed;
  bottom: 100px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 2000;
  pointer-events: none;
`;
const ToastBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  padding: 14px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  min-width: 240px;
`;
const ToastText = styled.p`
  font-size: 14px;
  color: #1E2A32;
  font-weight: 500;
`;
